import React, { type FunctionComponent, useState } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { css } from '@emotion/react'
import { borderRadius, colors } from '@retailer-platform/shared-components'
import moment, { type Moment } from 'moment'
import { DateRangePicker } from 'react-dates'
import { useDomainMessage } from 'domains/collections/utils/domain/intl'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { CollectionsFormRow } from 'domains/collections/components/collections-form/components/form-row/CollectionsFormRow'
import { collectionsFormTestIds } from 'domains/collections/components/collections-form/utils/collectionsForm.utils'
import { CollectionsFormInputWrapper } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInputWrapper'

interface CollectionsDetailActiveDatesProps {
  register: FormContextValues<CollectionsFormData>['register']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  setValue: FormContextValues<CollectionsFormData>['setValue']
}

type DateType = Moment | null

type StartEndDate = {
  startDate: DateType
  endDate: DateType
}

const dateFormat = 'YYYY-MM-DD'

const wrapperStyles = css({
  alignItems: 'center',
  display: 'flex',

  // Overwrite some of the Date picker styles to fit with IDS
  '.DateRangePickerInput, .SingleDatePicker .DateInput_input': {
    borderRadius: borderRadius.X8,
    color: colors.GRAYSCALE.X50,
    fontSize: '13px', // use the same font size for range/single picker
    lineHeight: '13px',
    height: '46px',
  },

  '.DateRangePicker .DateInput_input__small': {
    color: colors.GRAYSCALE.X70,
  },
})

export const CollectionsDetailsActiveDates: FunctionComponent<
  CollectionsDetailActiveDatesProps
> = ({ register, setValue, getValues }) => {
  const originalStartDate = getValues('details.startDate')
  const originalEndDate = getValues('details.endDate')

  // Set dates if a response is returned from API, if not start date is today, and no end date is set
  // State is used for visual representation in the input field only.
  const [dates, setDates] = useState({
    startDate: originalStartDate ? moment(originalStartDate).utc() : null,
    endDate: originalEndDate ? moment(originalEndDate).utc() : null,
  })

  // Focused state for date range
  const [focusedInput, setFocusedInput] = useState(null)

  // Handle date picker range change (start/end date)
  const onDateRangeChange = (newDates: StartEndDate) => {
    // Set state for visual form field
    setDates(newDates)

    // Set value for react hook form
    setValue('details.startDate', newDates.startDate?.utc().format(dateFormat))
    setValue('details.endDate', newDates.endDate?.utc().format(dateFormat))
  }

  return (
    <CollectionsFormRow>
      <div css={wrapperStyles}>
        <CollectionsFormInputWrapper
          label={useDomainMessage('collectionsDomain.collectionsForm.section.details.date')}
        >
          <DateRangePicker
            small
            withPortal
            minimumNights={0}
            startDateId="details.startDate"
            endDateId="details.endDate"
            startDate={dates.startDate}
            endDate={dates.endDate}
            focusedInput={focusedInput}
            onFocusChange={focusedInput => setFocusedInput(focusedInput)}
            onDatesChange={newDates => onDateRangeChange(newDates)}
            showClearDates
          />
        </CollectionsFormInputWrapper>
        <input
          name="details.startDate"
          ref={register({ required: false })}
          type="hidden"
          data-testid={collectionsFormTestIds.startDateField}
        />
        <input
          name="details.endDate"
          ref={register({ required: false })}
          type="hidden"
          data-testid={collectionsFormTestIds.endDateField}
        />
      </div>
    </CollectionsFormRow>
  )
}
